import { useRef, useCallback } from 'react';
import { MediaItem, formatFileSize } from '../../types/sound-management-types';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Play, Square, Trash2, AlertTriangle, Volume2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@/core/lib/utils';

interface SoundGridProps {
  sounds: MediaItem[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  playingMediaId: number | null;
  /** When true, group sounds by language with section headers */
  groupByLanguage?: boolean;
  onPageChange: (page: number) => void;
  onPlay: (sound: MediaItem) => void;
  onStop: () => void;
  onDelete: (sound: MediaItem) => void;
  onRetry: () => void;
}

export function SoundGrid({
  sounds,
  isLoading,
  error,
  totalCount,
  currentPage,
  pageSize,
  playingMediaId,
  groupByLanguage = false,
  onPageChange,
  onPlay,
  onStop,
  onDelete,
  onRetry,
}: SoundGridProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayClick = useCallback(
    (sound: MediaItem) => {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      if (playingMediaId === sound.mediaId) {
        // Was playing this one, just stop
        onStop();
        return;
      }

      // Start playing new audio
      const audio = new Audio(sound.s3Url);
      audioRef.current = audio;

      audio.addEventListener('ended', () => {
        onStop();
        audioRef.current = null;
      });

      audio.addEventListener('error', () => {
        onStop();
        audioRef.current = null;
      });

      audio.play().catch(() => {
        onStop();
        audioRef.current = null;
      });

      onPlay(sound);
    },
    [playingMediaId, onPlay, onStop]
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  // Group sounds by language code
  const groupedSounds = groupByLanguage
    ? sounds.reduce((groups, sound) => {
        const lang = sound.languageCode.toUpperCase();
        if (!groups[lang]) {
          groups[lang] = [];
        }
        groups[lang].push(sound);
        return groups;
      }, {} as Record<string, MediaItem[]>)
    : null;

  // Sort language keys alphabetically, but put EN first
  const sortedLanguageKeys = groupedSounds
    ? Object.keys(groupedSounds).sort((a, b) => {
        if (a === 'EN') return -1;
        if (b === 'EN') return 1;
        return a.localeCompare(b);
      })
    : [];

  // Loading state
  if (isLoading) {
    return (
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Language</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>File Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Uploaded</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-28')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className={cn(GRID_STYLES.row.default, `border-b ${GRID_STYLES.border}`)}>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-12" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-48" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-16" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-24" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-8 w-20" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-8 text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-4 text-lg font-medium text-red-800">Failed to load sounds</h3>
        <p className="mt-2 text-sm text-red-600">{error}</p>
        <Button onClick={onRetry} variant="outline" className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty state
  if (sounds.length === 0) {
    return (
      <div className="rounded-md border border-[#E8E8E8] bg-gray-50 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
          <Volume2 className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No sounds found</h3>
        <p className="mt-2 text-sm text-gray-500">
          Upload sound files to get started.
        </p>
      </div>
    );
  }

  // Helper to render a single sound row
  const renderSoundRow = (sound: MediaItem, showLanguage: boolean = true) => (
    <tr
      key={sound.mediaId}
      className={cn(
        GRID_STYLES.row.default,
        GRID_STYLES.row.hover,
        `border-b ${GRID_STYLES.border}`,
        'transition-colors'
      )}
    >
      {showLanguage && (
        <td className={cn(GRID_STYLES.cell.base, 'font-mono text-sm uppercase')}>
          {sound.languageCode}
        </td>
      )}
      <td className={GRID_STYLES.cell.base}>
        <div className="flex items-center gap-2">
          <Volume2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="truncate" title={sound.originalFileName}>
            {sound.originalFileName}
          </span>
        </div>
      </td>
      <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
        {formatFileSize(sound.fileSizeBytes)}
      </td>
      <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
        {formatDate(sound.created)}
      </td>
      <td className={GRID_STYLES.cell.base}>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePlayClick(sound)}
            className={cn(
              'h-8 w-8 p-0',
              playingMediaId === sound.mediaId && 'bg-blue-100 text-blue-600'
            )}
            title={playingMediaId === sound.mediaId ? 'Stop' : 'Play'}
          >
            {playingMediaId === sound.mediaId ? (
              <Square className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(sound)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete sound"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );

  // Data state
  return (
    <div className="space-y-4">
      {groupByLanguage && groupedSounds ? (
        // Grouped view - render each language as a section
        <div className="space-y-6">
          {sortedLanguageKeys.map((langCode) => (
            <div key={langCode} className="rounded-md border border-[#E8E8E8] overflow-hidden">
              {/* Language group header */}
              <div className={cn(GRID_STYLES.row.group, 'px-4 py-2 border-b', GRID_STYLES.border)}>
                <span className="font-semibold text-sm">
                  {langCode} <span className="text-gray-500 font-normal">({groupedSounds[langCode].length} sound{groupedSounds[langCode].length !== 1 ? 's' : ''})</span>
                </span>
              </div>
              <table className="w-full">
                <thead className={GRID_STYLES.header.background}>
                  <tr>
                    <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>File Name</th>
                    <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
                    <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Uploaded</th>
                    <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-28')}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedSounds[langCode].map((sound) => renderSoundRow(sound, false))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ) : (
        // Flat view - single table with language column
        <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
          <table className="w-full">
            <thead className={GRID_STYLES.header.background}>
              <tr>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Language</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>File Name</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Uploaded</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-28')}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sounds.map((sound) => renderSoundRow(sound, true))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className={cn('flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(0)} disabled={currentPage === 0}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]' : 'text-[#262626] hover:bg-[#FFFBEB]'}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum + 1}
              </Button>
            ))}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage + 1} of {totalPages} pages</span>
          </div>
          <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}
